import axios from "axios"
import { Map, fromJS } from "immutable"

import * as djangioActionCreators from "shared/djangio/action-creators"
import { segue, replace } from "global-action-creators"

import WebSearchActionTypes, {
    patchDeleteContact,
    patchDeleteEvent,
    patchDeleteFile,
    patchDeleteGrassrootsAction,
    patchDeleteNote,
    patchDeletePublicOrganization,
    patchSupporterAction,
    postSavedSearchTypes,
    patchSavedSearchTypes,
    loadVisualizationDataTypes,
    toggleTableViewActionDict,
    toggleHideFilterSetActionDict,
} from "app/static/frontend/search-new/action-types"
import SharedSearchActionTypes from "shared/search/action-types"
import GlobalActionTypes from "global-action-types"

import { getSearchPathFromQDT } from "search-new/routes"

import { getPlaceholderForDataType, generatePlaceholders } from "search-new/helperFunctions"
import { filterHasValue, stringifyFilterValue } from "shared/search/helperFunctions"

import {
    convertFilterKeysToFilterIds,
    updateFilter,
    resetFilters,
    buildRequestAndUrl,
    triggerDataRefresh,
} from "shared/search/action-creators"

import { selectCurrentFilters } from "shared/search/selectors"

import { selectVisualizationData, selectStoredGraphs, selectNextStoredGraphOrderNumber } from "search-new/selectors"

// constants
import { swalContents } from "search-new/components/helpTexts"
import { sendAction } from "shared/djangio/action-creators"
import { MODEL_NAME_RECEIPT } from "shared/djangio/moneyballHelpers"

/**
 * segues the user to the search feature.
 * @param   { initialFilter: Object, quorumDataType: Number }
 * initialFilter: any filters in {filterKey: value} that should be passed to Search.
 * quorumDataType: specifies the quorumDataType of the next filterableListView.
 *      (ie, 2 will give you BillFLV)
 */
export function segueToSearch(
    { initialFilter = {}, quorumDataType = undefined } = {
        initialFilter: {},
        quorumDataType: undefined,
    },
) {
    return (dispatch, getState) => {
        // pushing the previous page onto our internal nav stack,
        // so the 'x' button works.
        const prevPageUrl = window.location.pathname
        dispatch({ type: GlobalActionTypes.PUSH_PAGE, nextPage: { prevPageUrl } })

        // if no quorumDataType is specified, go to the most recent
        // flv loaded. If that doesn't exist, it'll go to the user's default search.
        const nextSearchURL = quorumDataType
            ? getSearchPathFromQDT(quorumDataType)
            : getState().search.get("nextSearchURL")

        // stringify each and every filter value, in the case we're coming from
        // lists or something else that might contain objects
        const formattedFilters = Object.entries(initialFilter).reduce((acc, [key, value]) => {
            const formattedValue = stringifyFilterValue(value)
            if (formattedValue) {
                acc[key] = formattedValue
            }
            return acc
        }, {})

        dispatch(segue({ pathname: nextSearchURL, query: formattedFilters }))
    }
}

/**
 * sets the search bar term.
 * @param   {string}    searchTerm  the desired text of the search bar
 */
export function setSearchTerm(searchTerm) {
    return {
        type: WebSearchActionTypes.SET_SEARCH_TERM,
        searchTerm,
    }
}

export function clearSearchTerm() {
    return { type: WebSearchActionTypes.CLEAR_SEARCH_TERM }
}

/**
 * sets the next search URL. Used in case a user generically clicks
 * into search -- we need to know where to sent them.
 * @param   {Number}    qdt  the quorum data type of the next filterable list view
 */
export function setNextSearchURL(qdt) {
    // getSearchPathFromQDT will use the qdt of the FLV, and
    // return either the search URL if it exists or
    // the default search path if not.
    return {
        type: WebSearchActionTypes.SET_NEXT_SEARCH_URL,
        url: getSearchPathFromQDT(qdt),
    }
}

/**
 * sets the placeholder text for the search bar. the text will be different
 * depending on what search you're in. this also stores the qdt and current
 * uniqueFlvId so that the main search bar has the necessary info to interact
 * with the currentFLV
 * @param   {Number}    qdt  the quorum data type of the current search.
 */
export function setSearchBarData(qdt, providedText, uniqueFlvId) {
    const placeholderText = qdt ? getPlaceholderForDataType(qdt) : providedText || generatePlaceholders()

    return {
        type: WebSearchActionTypes.SET_PLACEHOLDER_TEXT,
        text: placeholderText,
        qdt,
        uniqueFlvId,
    }
}

/**
 * clears the placeholder text. Seems fairly self-explainatory.
 */
export function clearPlaceholderText() {
    return {
        type: WebSearchActionTypes.SET_PLACEHOLDER_TEXT,
        text: undefined,
    }
}

/**
 * This might not need to be here once the search bar gets updated.
 */
export function setSearchHelpOpenStatus(status) {
    return {
        type: WebSearchActionTypes.SET_HELP_OPEN_STATUS,
        status,
    }
}

/**
 * stores the positions of the scrollbar in the filterable list view.
 * @param   {string}    uniqueFlvId the id of the FilterableListView
 * @param   {Object}    scrollTopPositions  the positions of listView, filterSet
 */
export function storeScrollTopPositions(uniqueFlvId, scrollTopPositions) {
    return {
        type: WebSearchActionTypes.STORE_SCROLL_TOP_POSITIONS,
        uniqueFlvId,
        scrollTopPositions,
    }
}

export const postSavedSearch = (kwargs) =>
    djangioActionCreators.resourcePostList(DjangIO.app.userdata.models.SafedSearch, postSavedSearchTypes, kwargs)

export const patchSavedSearch = (savedSearchId, kwargs) =>
    djangioActionCreators.resourcePatchDetail(
        DjangIO.app.userdata.models.SafedSearch,
        patchSavedSearchTypes,
        savedSearchId,
        kwargs,
    )

/**
 * Modifies the URL upon the filters changing!
 *
 * @name modifySearchQueryURL
 * @function
 * @param {string} uniqueFlvId - Unique identifier for the FLV.
 */

export function modifySearchQueryURL(uniqueFlvId) {
    return (dispatch, getState) => {
        const route = window.location.pathname
        const currentFilters = selectCurrentFilters(getState(), { uniqueFlvId })

        dispatch(
            replace({
                pathname: route,
                query: currentFilters.toJS(),
            }),
        )
    }
}

/**
 * Registers the filters provided to us by the URL.
 *
 * @name applyFiltersFromURL
 * @function
 */
export function applyFiltersFromURL(uniqueFlvId) {
    return (dispatch, getState) => {
        const urlFilters = fromJS(Object.fromEntries(new URLSearchParams(window.location.search)))
        const currentFilters = selectCurrentFilters(getState(), { uniqueFlvId })
        const flvSlice = getState().search.getIn(["filterableListViews", uniqueFlvId])

        const isSearchify = flvSlice.get("searchify", Map()).size > 0

        // if there are urlFilters and they are different from what is currently in the store,
        // that means we're passing a new query to our FLV! Clear out the store.
        if (urlFilters.size && !urlFilters.equals(currentFilters)) {
            // if there are a whole new set of filters passed in from
            // the URL, they override all previously existing filters.
            if (currentFilters.size) {
                dispatch(resetFilters(uniqueFlvId, true))
            }

            // we've got to filter out any model default filters that have snuck into
            // our filters via lists, alerts, or anything else that gets its data from
            // the backend's safed searches.
            const defaultFilters = fromJS(DjangIO.modelAtPath(flvSlice.get("resourcePath")).default_filters)
            const permanentFilters = flvSlice.get("permanentFilters")
            const backgroundFilters = defaultFilters.merge(permanentFilters).map((value) => stringifyFilterValue(value))

            const foregroundFilters = urlFilters.filter((value, key) => value !== backgroundFilters.get(key))

            const processedURLFilters = convertFilterKeysToFilterIds(getState().search, uniqueFlvId, foregroundFilters)

            // Reset the Search Term to whatever the advanced search filter is. This is necessary when segueing between
            // searches of the same dataset, but different filters (ie: Copilot Analyze).
            dispatch(setSearchTerm(processedURLFilters.getIn(["AdvancedSearchFilter", "value"])))

            dispatch({
                type: SharedSearchActionTypes.APPLY_EXTERNAL_FILTERS,
                uniqueFlvId,
                filters: processedURLFilters,
            })

            // now that we have our URL filters processed, clear out anything from the URL that doesn't belong
            dispatch(modifySearchQueryURL(uniqueFlvId))

            return true
        } else if (!urlFilters.size && currentFilters.size && !isSearchify) {
            // otherwise, if there's no URL but filters exist in the store,
            // modify the search URL accordingly! (For instance, selecting some filters,
            // segueing away from the FLV, and then clicking on the filter again.)
            dispatch(modifySearchQueryURL(uniqueFlvId))
        }

        return undefined
    }
}

// allows us to set Inline-specific key-value pairs in the redux store
export const updateInlineAppearance = (uniqueFlvId, inlineId, newAppearanceValue) => ({
    type: WebSearchActionTypes.UPDATE_INLINE_APPEARANCE,
    uniqueFlvId,
    inlineId,
    newAppearanceValue,
})

/**
 * action creator for the Inline options button that allows a user to delete a custom Event
 * @param   {object} datum the values from the database
 * @param   {string} uniqueFlvId the id of the FilterableListView
 * @param   {string} inlineId the id of the parent Inline
 *
 * @returns {dispatch} dispatches the action to the Redux store
 */
export const deleteEvent = (datum, uniqueFlvId, inlineId) => async (dispatch) => {
    const deleteEventConfig = {
        ...swalContents.deleteEvent,
        title: `Are you sure you want to delete ${datum.get("plain_subject")}?`,
    }
    const isConfirm = await swal(deleteEventConfig)
    if (isConfirm) {
        try {
            const archived = true
            const id = datum.get("id")
            await dispatch(
                djangioActionCreators.resourcePatchDetail(
                    DjangIO.app.events.models.Event,
                    patchDeleteEvent,
                    id,
                    { archived },
                    { archived, id },
                ),
            ).then(() => {
                dispatch(updateInlineAppearance(uniqueFlvId, inlineId, { isHidden: true }))
                swal(swalContents.successDeleteEvent)
            })
        } catch (error) {
            BACKENDERROR(error, swalContents.failDeleteEvent)
        }
    }
}

/**
 * action creator for the Inline options button that allows a user to delete a Note interaction
 * @param   {object} datum the values from the database
 * @param   {string} uniqueFlvId the id of the FilterableListView
 * @param   {string} inlineId the id of the parent Inline
 *
 * @returns {dispatch} dispatches the action to the Redux store
 */
export const deleteNote = (datum, uniqueFlvId, inlineId) => async (dispatch) => {
    const isConfirm = await swal(swalContents.deleteNote)
    if (isConfirm) {
        try {
            const archived = true
            const id = datum.get("id")
            await dispatch(
                djangioActionCreators.resourcePatchDetail(
                    DjangIO.app.userdata.notes.models.Note,
                    patchDeleteNote,
                    id,
                    { archived },
                    { archived, id },
                ),
            ).then(() => {
                dispatch(updateInlineAppearance(uniqueFlvId, inlineId, { isHidden: true }))
                swal(swalContents.successDeleteNote)
                dispatchEvent(new Event("note-deleted"))
            })
        } catch (error) {
            BACKENDERROR(error, swalContents.failDeleteNote)
        }
    }
}

/**
 * action creator for the Inline options button that allows a user to delete a Note interaction
 * @param   {object} datum the values from the database
 * @param   {string} uniqueFlvId the id of the FilterableListView
 * @param   {string} inlineId the id of the parent Inline
 *
 * @returns {dispatch} dispatches the action to the Redux store
 */
export const deleteGrassrootsAction = (datum, uniqueFlvId, inlineId) => async (dispatch) => {
    const isConfirm = await swal(swalContents.deleteGrassrootsAction)
    if (isConfirm) {
        try {
            const archived = true
            const id = datum.get("id")
            await dispatch(
                djangioActionCreators.resourcePatchDetail(
                    DjangIO.app.grassroots.models.GrassrootsSupporterAction,
                    patchDeleteGrassrootsAction,
                    id,
                    { archived },
                    { archived, id },
                ),
            ).then(() => {
                dispatch(updateInlineAppearance(uniqueFlvId, inlineId, { isHidden: true }))
                swal(swalContents.successDeleteGrassrootsAction)
            })
        } catch (error) {
            BACKENDERROR(error, swalContents.failGrassrootsAction)
        }
    }
}

/**
 * action creator for the Inline options button that allows a user to delete a File attachment
 * @param   {object}   datum the values from the database
 * @param   {string}   uniqueFlvId the id of the FilterableListView
 * @param   {string}   inlineId the id of the parent Inline
 *
 * @returns {dispatch} dispatches the action to the Redux store
 */
export const deleteFile = (datum, uniqueFlvId, inlineId) => async (dispatch) => {
    const isConfirm = await swal(swalContents.deleteFile)
    if (isConfirm) {
        try {
            const archived = true
            const id = datum.get("id")
            await dispatch(
                djangioActionCreators.resourcePatchDetail(
                    DjangIO.app.userdata.models.NonOutboxAttachment,
                    patchDeleteFile,
                    id,
                    { archived },
                    { archived, id },
                ),
            ).then(() => {
                dispatch(updateInlineAppearance(uniqueFlvId, inlineId, { isHidden: true }))
                swal(swalContents.successDeleteFile)
            })
        } catch (error) {
            BACKENDERROR(error, swalContents.failDeleteFile)
        }
    }
}

/**
 * action creator for the Inline options button that allows a user to update a File attachment
 * @param   {object}   data file to replace the original file and other information
 * @param   {object}   successCallack callback function when the request is successful
 *
 * @returns {dispatch} dispatches the action to the Redux store
 */
export const updateFile = (data, successCallback) => async (dispatch) => {
    const isConfirm = await swal(swalContents.updateFile)
    if (isConfirm) {
        $.ajax({
            data,
            url: `/nonoutboxattachment/update_file/`,
            type: "POST",
            cache: false,
            processData: false,
            contentType: false,
            success: (responseData) => {
                const newData = JSON.parse(responseData)

                if (newData.error === null || newData.error === undefined) {
                    if (successCallback) {
                        successCallback()
                    } else {
                        swal(swalContents.successReplaceFile)
                    }
                }

                // user canceled upload
                return []
            },
            error(jqXHR, textStatus) {
                swal({
                    title: "Error",
                    text: jqXHR.responseText,
                    icon: "error",
                })
            },
        })
    }
}

/**
 * action creator for the Inline options button that allows a user to delete a File attachment
 * @param   {object}   datum the values from the database
 * @param   {string}   uniqueFlvId the id of the FilterableListView
 * @param   {string}   inlineId the id of the parent Inline
 *
 * @returns {dispatch} dispatches the action to the Redux store
 */
export const deleteContact = (datum, uniqueFlvId, inlineId) => async (dispatch) => {
    const isConfirm = await swal(swalContents.deleteContact)
    if (isConfirm) {
        try {
            const archived = true
            const id = datum.get("id")
            await dispatch(
                djangioActionCreators.resourcePatchDetail(
                    DjangIO.app.grassroots.models.NewSupporter,
                    patchDeleteContact,
                    id,
                    { archived },
                    { archived, id },
                ),
            ).then(() => {
                dispatch(updateInlineAppearance(uniqueFlvId, inlineId, { isHidden: true }))
                swal(swalContents.successDeleteContact)
            })
        } catch (error) {
            BACKENDERROR(error, swalContents.failDeleteContact)
        }
    }
}

/**
 * action creator for the Inline options button that allows a user to delete a custom Public Organization
 * @param   {object}   datum the values from the database
 * @param   {string}   uniqueFlvId the id of the FilterableListView
 * @param   {string}   inlineId the id of the parent Inline
 *
 * @returns {dispatch} dispatches the action to the Redux store
 */
export const deletePublicOrganization = (datum, uniqueFlvId, inlineId) => async (dispatch) => {
    const isConfirm = await swal(swalContents.deletePublicOrganization)
    if (isConfirm && datum.get("organization_type") === DjangIO.app.person.types.PublicOrganizationType.custom.value) {
        try {
            const archived = true
            const id = datum.get("id")
            await dispatch(
                djangioActionCreators.resourcePatchDetail(
                    DjangIO.app.person.models.PublicOrganization,
                    patchDeletePublicOrganization,
                    id,
                    { archived },
                    { archived, id },
                ),
            ).then(() => {
                dispatch(updateInlineAppearance(uniqueFlvId, inlineId, { isHidden: true }))
                swal(swalContents.successDeletePublicOrganization)
            })
        } catch (error) {
            BACKENDERROR(error, swalContents.failDeletePublicOrganization)
        }
    }
}

/**
 * action creator for the GrassrootsAction Inline options button that allows a user to Approve & Send a grassroots action
 * @param   {object}   datum the values from the database
 * @param   {string}   uniqueFlvId the id of the FilterableListView
 * @param   {string}   quorumDataType the qdt of the FLV
 *
 * @returns {dispatch} dispatches the action to the Redux store
 */
export const updateSupporterAction = (datum, uniqueFlvId, quorumDataType) => async (dispatch) => {
    const isConfirm = await swal(swalContents.patchSupporterAction)
    if (isConfirm) {
        try {
            const confirmed = true
            const id = datum.get("id")
            await dispatch(
                djangioActionCreators.resourcePatchDetail(
                    DjangIO.app.grassroots.models.GrassrootsSupporterAction,
                    patchSupporterAction,
                    id,
                    { confirmed },
                    { confirmed, id },
                ),
            ).then(() => {
                dispatch(triggerDataRefresh(uniqueFlvId, DjangIO.newResourceFromDataType(quorumDataType)))
                swal(swalContents.successPatchSupporterAction)
            })
        } catch (error) {
            BACKENDERROR(error, swalContents.failPatchSupporterAction)
        }
    }
}

/*
 * Visualization stuff here!
 */

/**
 * sets the active visualization to the vizScene specified
 * @param   {string}   uniqueFlvId the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 */
export function setActiveVisualization(uniqueFlvId, uniqueVizSceneId) {
    return {
        type: WebSearchActionTypes.SET_ACTIVE_VISUALIZATION,
        uniqueFlvId,
        uniqueVizSceneId,
    }
}

/**
 * registers the visualization with the store
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {string}   vizualizationKey the key that gets passed in the API call specifying the viz type
 * @param   {string}   variableRequestKey   a secondary key that gets passed to the API as well
 * @param   {Array<string>}   visualizationFilterIds  an array of ids of the filters the vizualization interacts with
 * @param   {boolean}   shouldIgnoreFilters boolean indicating whether or not an api call should incorporate the viz filters
 * @param   {object}   extraData object of any extra data that should be stored in the viz slice's extraData
 */
export function registerVisualization(
    uniqueFlvId,
    uniqueVizSceneId,
    visualizationKey,
    variableRequestKey,
    visualizationFilterIds,
    shouldIgnoreFilters,
    initialExtraData,
) {
    return {
        type: WebSearchActionTypes.REGISTER_VISUALIZATION,
        uniqueFlvId,
        uniqueVizSceneId,
        visualizationKey,
        variableRequestKey,
        visualizationFilterIds,
        shouldIgnoreFilters,
        initialExtraData,
    }
}

/**
 * registers the visualization with the store
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {Object}   keys     object of whatever additional keys you want passed to the api
 */
export function setSecondaryGraphKeys(uniqueFlvId, uniqueVizSceneId, keys) {
    return {
        type: WebSearchActionTypes.SET_SECONDARY_GRAPH_KEYS,
        uniqueFlvId,
        uniqueVizSceneId,
        keys,
    }
}

/**
 * loads the visualization data!
 * @param   {Object}   resource     the DjangIO resource
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {string}   vizualizationKey the key that gets passed in the API call specifying the viz type
 * @param   {Array<string>} ignoredFilterIds    array of filter ids that should not be used when fetching data
 * @param   {Object}   additionalGraphKeys  other keys (and their values) that should also be passed in the API.
 * @param   {Function} modifyDataOnLoad  function that that'll process the data on load.
 */
export function loadVisualizationData({
    resource,
    uniqueFlvId,
    uniqueVizSceneId,
    visualizationKey,
    ignoredFilterIds,
    additionalGraphKeys = {},
    modifyDataOnLoad,
}) {
    return (dispatch, getState) => {
        const { url, request } = buildRequestAndUrl(getState(), resource, uniqueFlvId, ignoredFilterIds)

        return dispatch(
            djangioActionCreators.resourceGetList(
                request,
                loadVisualizationDataTypes,
                { [visualizationKey]: true, ...additionalGraphKeys },
                { uniqueFlvId, uniqueVizSceneId, modifyDataOnLoad },
                true, // get from cache if exists
            ),
        )
    }
}

/**
 * resets the filters that the visualization is associated with (for instance,
 * a map graph will reset the region filter)
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 */
export function resetVisualizationFilters(uniqueFlvId, uniqueVizSceneId) {
    return (dispatch, getState) => {
        const vizFilterIds = getState().search.getIn([
            "filterableListViews",
            uniqueFlvId,
            "visualizations",
            uniqueVizSceneId,
            "visualizationFilterIds",
        ])

        // if there are two filters we need to clear simultaneously, use reset select.
        // otherwise, just use update filter (for multi-filter stuff!)
        if (vizFilterIds.size === 1) {
            dispatch(updateFilter(vizFilterIds.first(), uniqueFlvId, "", false))
        } else {
            dispatch({
                type: SharedSearchActionTypes.RESET_SELECT_FILTERS,
                uniqueFlvId,
                uniqueFilterIds: vizFilterIds,
            })
        }
    }
}

/**
 * clears whatever visualization items are currently selected
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 */
export function clearVisualizationSelection(uniqueFlvId, uniqueVizSceneId) {
    return {
        type: WebSearchActionTypes.CLEAR_VISUALIZATION_SELECTION,
        uniqueFlvId,
        uniqueVizSceneId,
    }
}

/**
 * stores whatever visualization items that were just selected
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {Array -- subject to change} selectedValues the selected values
 */
export function updateVisualizationSelection(uniqueFlvId, uniqueVizSceneId, selectedValues) {
    return {
        type: WebSearchActionTypes.UPDATE_VISUALIZATION_SELECTION,
        uniqueFlvId,
        uniqueVizSceneId,
        selectedValues,
    }
}

/**
 * takes the selected visualization items from the store, and then
 * applies those visualization items to the filters associated with the
 * filters. Presently only works if the visualization has a single
 * filter associated with it.
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 */
export function applyFiltersFromVisualization(uniqueFlvId, uniqueVizSceneId) {
    return (dispatch, getState) => {
        const vizSlice = getState().search.getIn([
            "filterableListViews",
            uniqueFlvId,
            "visualizations",
            uniqueVizSceneId,
        ])

        const vizFilterIds = vizSlice.get("visualizationFilterIds").toJS()
        const selectedValues = vizSlice.get("selectedValues").toJS()

        if (vizFilterIds > 1) {
            console.error("This hasn't been implemented yet for more than one filter!")
        }

        const filterId = vizFilterIds[0]
        const serializationKey = getState().search.getIn([
            "filterableListViews",
            uniqueFlvId,
            "filters",
            filterId,
            "serializationKey",
        ])
        const serializationFunc = getState().search.getIn(["serializeFunctions", serializationKey])

        const serializedValue = serializationFunc(selectedValues)
        const hasValue = filterHasValue(serializedValue)

        dispatch(updateFilter(filterId, uniqueFlvId, serializedValue, hasValue))
        dispatch(clearVisualizationSelection(uniqueFlvId, uniqueVizSceneId))
    }
}

/**
 * pulls the current visualization and makes a copy of it in a section
 * of the store. This is so users can layer multiple graphs atop each other.
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {string}   title   the user-specified title of the graph
 */
export function storeCurrentGraph(uniqueFlvId, uniqueVizSceneId) {
    return (dispatch, getState) => {
        // TODO: store current filters, but minus some filters if we don't want them
        // (for example, line graph dates.)
        const currentFilters = selectCurrentFilters(getState(), { uniqueFlvId })
        const data = selectVisualizationData(getState(), { uniqueFlvId, uniqueVizSceneId })
        const storedGraphs = selectStoredGraphs(getState(), { uniqueFlvId, uniqueVizSceneId })
        const nextStoredGraphOrderNumber = selectNextStoredGraphOrderNumber(getState(), {
            uniqueFlvId,
            uniqueVizSceneId,
        })

        if (storedGraphs.size >= 10) {
            return swal({
                icon: "warning",
                title: "Graph limit reached!",
                text: "There is a 10 graph limit. Please removed some stored graphs to continue.",
            })
        }

        const label = "Graph"

        swal({
            title: `Save Current ${label}`,
            text: `Please give your ${label.toLowerCase()} a title`,
            content: {
                element: "input",
                attributes: {
                    placeholder: "Title...",
                },
            },
            buttons: ["Cancel", true],
        }).then((input) => {
            // if cancel hit, do nothing
            if (input === null) {
                return
            } else if (input === "") {
                swal("Please enter a title", "", "error")
            } else if (storedGraphs.find((_, key) => key === input)) {
                swal(`A ${label.toLowerCase()} with this title already exists`, "", "error")
            } else {
                dispatch({
                    type: WebSearchActionTypes.STORE_CURRENT_GRAPH,
                    uniqueFlvId,
                    uniqueVizSceneId,
                    data,
                    currentFilters,
                    order: nextStoredGraphOrderNumber,
                    title: input,
                })
            }
        })
    }
}

/**
 * specifies that the stored graph with key {title} should be displayed.
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {string}   title   the user-specified title of the graph
 */
export function showStoredGraph(uniqueFlvId, uniqueVizSceneId, title) {
    return {
        type: WebSearchActionTypes.SHOW_STORED_GRAPH,
        uniqueFlvId,
        uniqueVizSceneId,
        title,
    }
}

/**
 * specifies that the stored graph with key {title} should be hidden.
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {string}   title   the user-specified title of the graph
 */
export function hideStoredGraph(uniqueFlvId, uniqueVizSceneId, title) {
    return {
        type: WebSearchActionTypes.HIDE_STORED_GRAPH,
        uniqueFlvId,
        uniqueVizSceneId,
        title,
    }
}

/**
 * renames the stored graph from current key to another key.
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {string}   title   the user-specified old title of the graph
 */
export function renameStoredGraph(uniqueFlvId, uniqueVizSceneId, title) {
    return (dispatch, getState) => {
        const storedGraphs = selectStoredGraphs(getState(), { uniqueFlvId, uniqueVizSceneId })

        swal({
            title: `Rename "${title}"`,
            text: `Please enter a new title`,
            content: {
                element: "input",
                attributes: {
                    placeholder: "Title...",
                },
            },
            buttons: ["Cancel", true],
        }).then((input) => {
            // if cancel hit, the input it blank, or the input is the same as the old key,
            // do nothing.
            if (!input || title === input) {
                return
            } else if (storedGraphs.find((_, key) => key === input)) {
                swal(`A graph with this title already exists`, "", "error")
            } else {
                dispatch({
                    type: WebSearchActionTypes.RENAME_STORED_GRAPH,
                    uniqueFlvId,
                    uniqueVizSceneId,
                    oldKey: title,
                    newKey: input,
                })
            }
        })
    }
}

/**
 * deletes the stored graph with key {title}
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {string}   title   the user-specified title of the graph
 */
export function deleteStoredGraph(uniqueFlvId, uniqueVizSceneId, title) {
    return (dispatch, getState) => {
        swal({
            title: `Are you sure you want to want to delete saved graph "${title}"?`,
            buttons: ["Cancel", true],
        }).then((confirm) => {
            if (confirm) {
                dispatch({
                    type: WebSearchActionTypes.DELETE_STORED_GRAPH,
                    uniqueFlvId,
                    uniqueVizSceneId,
                    title,
                })
            }
        })
    }
}

/**
 * deletes all stored graphs. rest in peace, graphs.
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 */
export function deleteAllStoredGraphs(uniqueFlvId, uniqueVizSceneId) {
    return (dispatch, getState) => {
        swal({
            title: "Are you sure you want to want to delete all graphs?",
            buttons: ["Cancel", true],
        }).then((confirm) => {
            if (confirm) {
                dispatch({
                    type: WebSearchActionTypes.DELETE_ALL_STORED_GRAPHS,
                    uniqueFlvId,
                    uniqueVizSceneId,
                })
            }
        })
    }
}

/**
 * generates a CSV of the current graph data.
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 */
export function downloadVisualizationCSV(uniqueFlvId, uniqueVizSceneId) {
    return (dispatch, getState) => {
        // this method uses data that's entirely from the store (nothing
        // gets passed in via props, outside of the flvId and sceneId).
        // That's why it's so cluttered. :( )
        const resource = DjangIO.modelAtPath(
            getState().search.getIn(["filterableListViews", uniqueFlvId, "resourcePath"]),
        )

        // Campaign Finance visualization download button
        // Extract data from charts, generate csv and download to browser
        if (resource.__name__ === MODEL_NAME_RECEIPT) {
            const chartsData = Array.from(
                getState().search.getIn([
                    "filterableListViews",
                    uniqueFlvId,
                    "visualizations",
                    uniqueVizSceneId,
                    "data",
                ]),
            )

            var dataArray = chartsData.map(function (d) {
                return Array.from(d.values()).join()
            })
            const deduplicatedData = dataArray.filter((value, index, array) => array.indexOf(value) === index)
            deduplicatedData.unshift(Array.from(chartsData[0].keys()).join())

            const anchor = document.createElement("a")
            anchor.href = window.URL.createObjectURL(new Blob([deduplicatedData.join("\n")], { type: "text/csv" }))
            anchor.download = "Search Results - Generated by Quorum.csv"
            return anchor.click()
        }

        const vizSlice = getState().search.getIn([
            "filterableListViews",
            uniqueFlvId,
            "visualizations",
            uniqueVizSceneId,
        ])
        const shouldIgnoreFilters = vizSlice.get("shouldIgnoreFilters")
        const visualizationKey = vizSlice.get("visualizationKey")
        const secondaryGraphKeys = vizSlice.get("secondaryGraphKeys").toJS()

        let ignoredFilterIds = []
        if (shouldIgnoreFilters) {
            ignoredFilterIds = vizSlice.get("visualizationFilterIds").toJS()
        }

        const { request } = buildRequestAndUrl(getState(), resource, uniqueFlvId, ignoredFilterIds)

        return request.filter({ [visualizationKey]: true, format: "simpleCSV", ...secondaryGraphKeys }).download()
    }
}

/**
 * stores extra visualization data. this is a way for graphs to store
 * additional information and have subsequent redux access to it.
 * @param   {string}   uniqueFlvId  the id of the FilterableListView
 * @param   {string}   uniqueVizSceneId the id of the visualization scene
 * @param   {Object}   extraData   whatever extra data we have, in object form
 */
export function updateExtraVisualizationData(uniqueFlvId, uniqueVizSceneId, extraData) {
    return {
        type: WebSearchActionTypes.STORE_EXTRA_VISUALIZATION_DATA,
        uniqueFlvId,
        uniqueVizSceneId,
        data: extraData,
    }
}

/*
 * MAP SPECIFIC ACTION CREATOR
 */

/**
 * loads the shape file for our map!
 * @param   {string}   url   the url of the shape file
 * @param   {function}  formatFunction  function that translates the response data
 *          into a map-readable dataset
 */
export function loadMapGeographyData(url, formatFunction) {
    return (dispatch, getState) => {
        axios
            .get(url)
            .then((response) => {
                dispatch({
                    type: WebSearchActionTypes.LOAD_MAP_GEOGRAPHY_DATA_SUCCESS,
                    url,
                    response: formatFunction(response),
                })
            })
            .catch((error) => {
                console.warn("Loading geography data failed: ", error)
            })
    }
}

/**
 * Makes a request and populates currentExactCount in slice with the response.
 *
 * @param {Object} resource
 * @param {String} uniqueFlvId the id of the FilterableListView
 */
const fetchExactCount = (dispatch, getState, resource, uniqueFlvId) => {
    const { request } = buildRequestAndUrl(getState(), resource, uniqueFlvId)
    dispatch({
        type: SharedSearchActionTypes.LOADING_EXACT_COUNT_START,
    })

    request.filter({ count_only: true, actual_count: true })
    request
        .GET()
        .then((response) => {
            dispatch({
                type: SharedSearchActionTypes.LOADING_EXACT_COUNT_SUCCESS,
                exactCount: response,
                uniqueFlvId,
            })
        })
        .catch(() => {
            dispatch({
                type: SharedSearchActionTypes.LOAD_EXACT_COUNT_FAIL,
                uniqueFlvId,
            })
        })
}

/**
 * Toggles the search count precision of the filterable list view.
 *
 * @param {Number} currentExactCount current exact search count, if it exists
 * @param {Boolean} oldShouldShowExactCount previous value
 * @param {Object} resource
 * @param {String} uniqueFlvId the id of the FilterableListView
 */
export const toggleSearchCountPrecision = (
    currentExactCount,
    isExactCountInaccessible,
    oldShouldShowExactCount,
    resource,
    uniqueFlvId,
) => {
    return (dispatch, getState) => {
        if (!currentExactCount) {
            fetchExactCount(
                // Send the request.
                dispatch,
                getState,
                resource,
                uniqueFlvId,
            )
        }
        if (isExactCountInaccessible) {
            swal({
                text: "Exact count could not be fetched. Viewing unabbreviated estimated count.",
                icon: "warning",
                button: "OK",
            })
        }
        dispatch({
            type: SharedSearchActionTypes.TOGGLE_SEARCH_COUNT_PRECISION,
            oldShouldShowExactCount,
            uniqueFlvId,
        })
    }
}

/**
 * toggleHideFilterSet - action creator that updates whether or not a user see the FilterSet in a search FilterableListView
 * @param {Function} sendAction - action creator for handling any request an action can handle (GET/POST/PATCH/DELETE)
 * @param {Boolean} shouldHideFilters - value to set for search_should_hide_filters
 * @param {Function} updateUserdata - action creator for updating userdata in the store
 */
export const toggleHideFilterSet = async ({ sendAction, shouldHideFilters, updateUserdata }) => {
    // optimistically update the search_should_hide_filters value in the store for faster UI response
    updateUserdata({ search_should_hide_filters: shouldHideFilters })
    try {
        await sendAction(DjangIO.app.userdata.models.QuorumUser, toggleHideFilterSetActionDict, {
            action: "update_user_information",
            kwargs: {
                userId: Userdata.id,
                updateFields: {
                    search_should_hide_filters: shouldHideFilters,
                },
            },
            method: "patch",
        })
    } catch (error) {
        // if there is an error, reset the store to have the original value
        updateUserdata({ search_should_hide_filters: !shouldHideFilters })
        BACKENDERROR(error)
    }
}

/**
 * toggleTableView - action creator that updates the whether user see search results in table view on frontend and backend
 * @param {Boolean} isTableView - value to set for search_is_table_view
 * @param {Function} sendAction - action creator for handling any request an action can handle (GET/POST/PATCH/DELETE)
 * @param {Function} updateUserdata - action creator for updating userdata in the store
 */
export const toggleTableView = async ({ isTableView, sendAction, updateUserdata }) => {
    // optimistically update the table view value in the store for faster UI response
    updateUserdata({ search_is_table_view: isTableView })
    try {
        await sendAction(DjangIO.app.userdata.models.QuorumUser, toggleTableViewActionDict, {
            action: "update_user_information",
            kwargs: {
                userId: Userdata.id,
                updateFields: {
                    search_is_table_view: isTableView,
                },
            },
            method: "patch",
        })
    } catch (error) {
        // if there is an error, reset the store to have the original value
        updateUserdata({ search_is_table_view: !isTableView })
        BACKENDERROR(error)
    }
}

export const setSearchFiltersTerm = ({ searchFilterTerm, uniqueFlvId }) => ({
    type: WebSearchActionTypes.SEARCH_FILTERS_TERM,
    searchFilterTerm,
    uniqueFlvId,
})

/**
 * setAiSearchApplied - action creator that will update redux store to differentiate between an AI search and regular search
 * @param {Boolean} isApplied - boolean value set for isApplied
 * @param {Object} filterKeys - object of filter keys and their respective values
 */
export const setAiSearchApplied = ({ isApplied, filterKeys }) => ({
    type: WebSearchActionTypes.AI_SEARCH_APPLIED,
    isApplied,
    filterKeys,
})

/**
 * aiSearchMakeGetRequest - fetch request to the magic search service, returns the response JSON
 * @param {String} query - query string that was typed by the user on the searchbar
 */
export const aiSearchMakeGetRequest = async (query) => {
    const response = await fetch(`/magic_search/?question=` + query, {
        method: "GET",
        credentials: "include",
    })

    // return empty object to indicate no response
    if (!response.ok) {
        return {}
    }

    return response.json()
}

export function setMobileFiltersOpenStatus(status) {
    return {
        type: WebSearchActionTypes.SET_MOBILE_FILTERS_OPEN_STATUS,
        status,
    }
}
